// @ts-nocheck
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  ICreateTeamRequest,
  IInviteMemberRequest,
  ITeam,
  ITeamNotificationUser,
  IUpdateTeamRequest,
  TeamMembersRoles,
  TeamUser,
} from "@/integration/team/interfaces/team.interface.ts";
import { IGameDTO } from "@/integration/game/interfaces/game.interface.ts";
import { ISearchPagination } from "@/integration/application/interfaces/searchPagination.interface.ts";

import teamService from "@/integration/team/services/team.service.ts";
import teamServiceNew from "@/integration/team/services/team.service.new.ts";
import useUser from "@/integration/user/hooks/useUser.ts";
import useAuth from "@/integration/user/hooks/useAuth.ts";
import useTeamPublic from "./useTeamPublic.ts";
import {
  optionsFetchTeamById,
  optionsFetchTeamByIdPublic,
  optionsFetchTeamNotifications,
} from "@/config/plugins/queryOptions.reactquery.ts";
import ToastContextExports from "@/context/ToastContext.tsx";

const useTeam = (teamId?: string) => {
  const queryClient = useQueryClient();
  const { searchUser } = useUser();
  const { isAuthenticated } = useAuth();
  const { teamMembers } = useTeamPublic(teamId);
  const { showToast } = ToastContextExports.useToast();
  const {
    postCreate,
    postSendInvite,
    postSendInvites,
    putUpdate,
    patchAcceptionInvitation,
    patchDeclineInvitation,
    deleteTeamMemberByUserId,
    patchRoleTeamMember,
    patchPositionTeamMember,
    getGamesTeam,
    searchMyTeamsByNameAndTag,
    deleteInvitation,
  } = teamService();

  const { getTeamInvitations, getTeamChampionshipRegistrations } = teamServiceNew();
  const navigator = useNavigate();

  const { data: teamNotificationUser } = useQuery(
    optionsFetchTeamNotifications(isAuthenticated)
  );

  const createTeam: SubmitHandler<ICreateTeamRequest> = async (
    values,
    event
  ): Promise<ITeam> => {
    try {
      event?.preventDefault();
      const result = await postCreate(values);
      showToast("Sucesso","Time criado com sucesso.", "success");

      if(result?.team_tag) {  
        navigator(`/my/teams/${result?.team_tag}`);
      } else {
        navigator(`/teams`);
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  const sendInvitations = async (
    teamId: string,
    data: IInviteMemberRequest[]
  ): Promise<void> => {
    try {
      if (!teamId) throw new Error("ID do time é obrigatório");
      await postSendInvites(teamId, data);
      showToast("Sucesso","Convites enviados com sucesso.", "success");
    } catch (error) {
      throw error;
    }
  };

  const sendInvitation = async (data: IInviteMemberRequest) => {
    await postSendInvite(teamId!, data);
    showToast("Sucesso","Convite enviado com Sucesso.", "success");
  };

  const acceptionInvitation = async (teamId: string, invitationId: string) => {
    await patchAcceptionInvitation(teamId, invitationId);
    queryClient.invalidateQueries(optionsFetchTeamNotifications(true));
    showToast("Sucesso","Convite Aceito.", "success");
  };

  const declineInvitation = async (teamId: string, invitationId: string) => {
    await patchDeclineInvitation(teamId, invitationId);
  };

  const deleteTeamMember = async (teamId: string, userId: string) => {
    await deleteTeamMemberByUserId(teamId, userId);
  };

  const invite = async (value: string) => {
    await sendInvitation({
      userId: value,
      role: TeamMembersRoles.PLAYER,
    });
  };

  const searchUsersTeamInvitation = (value: string): TeamUser[] | undefined => {
    if (!value.trim()) return [];

    const teamMembersId = teamMembers?.map((item) => item.entity.user_id);
    const listUsers = searchUser(value);

    // @ts-ignore
    const data = listUsers?.filter(
      (item) => !teamMembersId?.includes(item.entity.id)
    );
    return data;
  };

  const updateTeam = async (teamId: string, data: IUpdateTeamRequest) => {
    await putUpdate(teamId, data);
    showToast("Sucesso","Time atualizado com sucesso.", "success");
    queryClient.invalidateQueries(optionsFetchTeamByIdPublic(teamId));
  };

  const patchRoleMember = async (
    teamId: string,
    teamMemberId: string,
    data: TeamMembersRoles
  ) => {
    await patchRoleTeamMember(teamId, teamMemberId, data);
  };

  const patchPositionMember = async (
    teamId: string,
    teamMemberId: string,
    position: string
  ) => {
    await patchPositionTeamMember(teamId, teamMemberId, position);
  };

  const { data: teamProfile } = useQuery({
    ...optionsFetchTeamById(teamId!),
  });

  const getAllGamesTeam = (teamId: string) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<IGameDTO[], Error>({
      queryKey: ["teamGames", teamId],
      queryFn: () => getGamesTeam(teamId),
      enabled: Boolean(teamId),
      staleTime: 1000 * 60 * 5, // 5 minutos
      cacheTime: 1000 * 60 * 30, // 30 minutos
      refetchOnWindowFocus: false, // evita requisições excessivas
    });
  };

  const searchMyTeams = (searchTeamsParams: ISearchPagination) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<ITeam[], Error>({
      queryKey: ["myTeams", searchTeamsParams],
      queryFn: () => searchMyTeamsByNameAndTag(searchTeamsParams),
      enabled: Boolean(teamId),
      staleTime: 1000 * 60 * 5, // 5 minutos
      cacheTime: 1000 * 60 * 30, // 30 minutos
      refetchOnWindowFocus: false, // evita requisições excessivas
    });
  };

  // Get team invitations with pagination and filters
  const getTeamInvitationsWithFilters = (pagination: ISearchPagination) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<ITeamNotificationUser[], Error>({
      queryKey: ["teamInvitations", teamId, pagination],
      queryFn: () =>
        getTeamInvitations(teamId!, {
          status: pagination.status,
          page: pagination.page,
          limit: pagination.limit,
        }),
      enabled: Boolean(teamId),
      staleTime: 1000 * 60 * 5, // 5 minutos
      cacheTime: 1000 * 60 * 30, // 30 minutos
      refetchOnWindowFocus: false, // evita requisições excessivas
    });
  };

  // Cancel invitation
  const cancelInvitation = async (invitationId: string) => {
    try {
      await deleteInvitation(teamId!, invitationId);
      showToast("Sucesso","Convite excluído com sucesso.", "success");
      queryClient.invalidateQueries({ queryKey: ["teamInvitations", teamId] });
    } catch (error) {
      showToast("Erro","Erro ao excluir convite.", "error");
      throw error;
    }
  };

  // Resend invitation
  const resendInvitation = async (invitationId: string) => {
    try {
      // Get the invitation details first
      const invitations = await getTeamInvitations(teamId!, {});
      const invitation = invitations.find((inv) => inv.id === invitationId);

      if (invitation) {
        await postSendInvite(teamId!, {
          userId: invitation.user_id,
          role: invitation.role,
        });
        showToast("Convite reenviado com sucesso.", "", "success");
        queryClient.invalidateQueries({
          queryKey: ["teamInvitations", teamId],
        });
      }
    } catch (error) {
      showToast("Erro ao reenviar convite.", "error");
      throw error;
    }
  };

  /**
   * Get paginated championship registrations for a team
   * @param paginationParams Pagination parameters (page, limit)
   * @returns A query result with paginated championship registrations
   */
  const getTeamChampionshipRegistrationsPaginated = (paginationParams = { page: 1, limit: 10 }) => {
    return useQuery({
      queryKey: ['teamChampionshipRegistrations', teamId, paginationParams],
      queryFn: () => getTeamChampionshipRegistrations(
        teamId,
        paginationParams.page,
        paginationParams.limit
      ),
      enabled: Boolean(teamId),
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false, // avoid excessive requests
    });
  };

  return {
    createTeam,
    notificationByUser: teamNotificationUser,
    acceptionInvitation,
    sendInvitation,
    sendInvitations,
    declineInvitation,
    invite,
    searchUsersTeamInvitation,
    updateTeam,
    deleteTeamMember,
    patchRoleMember,
    patchPositionMember,
    getAllGamesTeam,
    searchMyTeams,
    teamProfile,
    getTeamInvitationsWithFilters,
    cancelInvitation,
    resendInvitation,
    getTeamChampionshipRegistrationsPaginated,
  };
};

export default useTeam;
